import { EventbriteHeader } from '@eventbrite/webapp-constants';
import { getLocaleFromRequest } from '@eventbrite/webapp-locales';
import { IncomingMessage } from 'http';

/**
 * The standalone homepage mounted at Next `basePath: '/home'`, which Next strips
 * from `req.url` before getServerSideProps runs. The host has no basePath, so
 * `req.url` keeps the `/home` mount prefix (and may carry a query string); strip
 * only the path prefix so `serverUrl` (used for canonical/OG/Twitter URLs and
 * the global-header `path`) reproduces the standalone's public URLs instead of
 * leaking `https://{host}/home…`.
 */
function stripSurfaceMount(url: string): string {
    const queryIndex = url.search(/[?#]/);
    const pathname = queryIndex === -1 ? url : url.slice(0, queryIndex);
    const suffix = queryIndex === -1 ? '' : url.slice(queryIndex);
    if (pathname === '/home' || pathname === '/home/') return `/${suffix}`;
    if (pathname.startsWith('/home/')) return `${pathname.slice('/home'.length)}${suffix}`;
    return url;
}

/**
 * The standalone ran at Next `basePath: '/home'`, which Next stripped from
 * `req.url` before `getServerSideProps` — so the global-header library and the
 * fallback header props saw `/` and `/fr_CA`, not `/home…`. Reproduce that by
 * returning a shallow context copy whose `req` inherits the original (methods
 * intact) with a mount-stripped `url`.
 */
export function withPublicSurfaceUrl<T extends { req?: IncomingMessage }>(context: T): T {
    if (!context.req) return context;
    // Keep the prototype chain (IncomingMessage methods) while copying own
    // enumerable props (headers, etc.) so the copy reads like the original.
    const req = Object.create(context.req) as IncomingMessage;
    Object.assign(req, context.req);
    req.url = stripSurfaceMount(context.req.url ?? '');
    return { ...context, req };
}

/**
 * Shape a public document URL the way the standalone emitted `og:url` for
 * `/it-lists/*`: the public host with the surface mount (`/home`) stripped, the
 * query removed, and exactly one trailing slash (the standalone ran
 * `trailingSlash: true`, and the NEW seam drops the trailing slash on the way
 * in). The standalone used the requested path verbatim for `og:url`; this
 * reproduces that without leaking the internal `/home/it-lists` mount.
 */
export function normalizePublicDocumentUrl(url: string): string {
    const withoutHash = url.split('#')[0];
    const pathOnly = withoutHash.split('?')[0];
    return pathOnly.endsWith('/') ? pathOnly : `${pathOnly}/`;
}

export function getRequestContext(req?: IncomingMessage) {
    const hostHeader = req?.headers[EventbriteHeader.EB_HOST] as string;
    const localeHeader = req?.headers[EventbriteHeader.EB_LANG] as string;
    const host = hostHeader || 'www.eventbrite.com';
    const tld = (host.match(/(?:evbqa|evbstage|eventbrite)(\.\S+)$/) || [])[1];
    const serverUrl = `https://${host}${req?.url ? stripSurfaceMount(req.url) : ''}`;
    const locale = localeHeader || (req ? getLocaleFromRequest(req) : '');

    return {
        tld,
        host,
        serverUrl,
        locale,
    };
}
